import React, { useState, useRef, useEffect, useCallback } from 'react';
import { useFormContext } from 'react-hook-form';
import { CustomIcon } from '../../../../UI';
import carrierService from '../../../../../services/carrierService';

const CarrierEmailsSection = ({ availableEmails = [] }) => {
  const { setValue, watch } = useFormContext();
  
  // Get current data from form
  const formCarrierEmails = watch('carrierEmails') || [];
  
  // State for managing carrier emails - sync with form
  const [selectedCarrierEmails, setSelectedCarrierEmails] = useState(formCarrierEmails);
  const [carrierEmailInput, setCarrierEmailInput] = useState('');
  const [showCarrierSuggestions, setShowCarrierSuggestions] = useState(false);
  const [carrierSuggestions, setCarrierSuggestions] = useState([]);
  const [showAllCarrierEmails, setShowAllCarrierEmails] = useState(false);
  const [carrierEmailError, setCarrierEmailError] = useState('');
  
  const carrierEmailInputRef = useRef(null);
  const carrierSuggestionsRef = useRef(null);

  // Sync selectedCarrierEmails with form data when form data changes
  useEffect(() => {
    if (Array.isArray(formCarrierEmails)) {
      setSelectedCarrierEmails(formCarrierEmails);
    }
  }, [formCarrierEmails]);

  // Function to validate email
  const validateEmail = (email) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };

  // Get email from a carrier
  const getCarrierEmail = (carrier) => {
    return carrier.email || '';
  };

  // Search carriers by email for email input
  const searchCarriersByEmail = useCallback(async (input) => {
    if (!input || input.length < 2) {
      setCarrierSuggestions([]);
      return;
    }
    
    try {
      const response = await carrierService.searchCarriers({ 
        email: input.trim(),
        limit: 10 
      });
      
      const carriers = response?.data?.data || response?.data || [];
      
      const carrierSuggestions = carriers
        .filter(carrier => {
          const carrierEmail = carrier.email || '';
          
          return carrierEmail && 
            carrierEmail.toLowerCase().includes(input.toLowerCase()) &&
            !selectedCarrierEmails.includes(carrierEmail);
        })
        .map(carrier => {
          const carrierEmail = carrier.email || '';
          
          return {
            email: carrierEmail,
            carrier: carrier
          };
        });
      
      setCarrierSuggestions(carrierSuggestions.slice(0, 5));
    } catch (error) {
      console.error('Error searching carriers:', error);
      setCarrierSuggestions([]);
    }
  }, [selectedCarrierEmails]);

  // Debounced email search
  useEffect(() => {
    const timeoutId = setTimeout(() => {
      if (carrierEmailInput.trim().length >= 2) {
        searchCarriersByEmail(carrierEmailInput);
      } else {
        setCarrierSuggestions([]);
      }
    }, 300);

    return () => clearTimeout(timeoutId);
  }, [carrierEmailInput, searchCarriersByEmail]);

  // Handle carrier email input change
  const handleCarrierEmailInputChange = (e) => {
    const value = e.target.value;
    setCarrierEmailInput(value);
    setCarrierEmailError('');
    
    if (value.trim()) {
      setShowCarrierSuggestions(true);
    } else {
      setShowCarrierSuggestions(false);
      setCarrierSuggestions([]);
    }
  };

  // Handle carrier email input focus
  const handleCarrierEmailInputFocus = () => {
    if (carrierEmailInput.trim().length >= 2) {
      setShowCarrierSuggestions(true);
    }
  };

  // Add carrier email
  const addCarrierEmail = (email, carrier = null) => {
    const trimmedEmail = email.trim();
    
    if (!trimmedEmail) {
      setCarrierEmailError('Email cannot be empty');
      return;
    }
    
    if (!validateEmail(trimmedEmail)) {
      setCarrierEmailError('Please enter a valid email address');
      return;
    }
    
    if (selectedCarrierEmails.includes(trimmedEmail)) {
      setCarrierEmailError('This email is already added');
      return;
    }
    
    const newEmails = [...selectedCarrierEmails, trimmedEmail];
    setSelectedCarrierEmails(newEmails);
    setValue('carrierEmails', newEmails, { shouldDirty: true, shouldValidate: true });
    
    setCarrierEmailInput('');
    setShowCarrierSuggestions(false);
    setCarrierSuggestions([]);
    setCarrierEmailError('');
  };

  // Remove carrier email
  const removeCarrierEmail = (emailToRemove) => {
    const newEmails = selectedCarrierEmails.filter(email => email !== emailToRemove);
    setSelectedCarrierEmails(newEmails);
    setValue('carrierEmails', newEmails, { shouldDirty: true, shouldValidate: true });
  };

  // Handle carrier suggestion selection
  const handleCarrierSuggestionClick = (suggestion) => {
    addCarrierEmail(suggestion.email, suggestion.carrier);
  };

  // Handle Enter key press for carrier emails
  const handleCarrierEmailKeyPress = (e) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      if (carrierSuggestions.length > 0) {
        handleCarrierSuggestionClick(carrierSuggestions[0]);
      } else {
        addCarrierEmail(carrierEmailInput);
      }
    } else if (e.key === 'Escape') {
      setShowCarrierSuggestions(false);
      setCarrierSuggestions([]);
    }
  };

  // Handle click outside carrier suggestions
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (carrierSuggestionsRef.current && !carrierSuggestionsRef.current.contains(event.target) &&
          carrierEmailInputRef.current && !carrierEmailInputRef.current.contains(event.target)) {
        setShowCarrierSuggestions(false);
        setCarrierSuggestions([]);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  return (
    <div className="carrier-emails-section">
      <h3 className="section-title">Carrier Emails</h3>
      
      <div className="email-input-container">
        <div className="email-input-wrapper">
          <input
            ref={carrierEmailInputRef}
            type="email"
            value={carrierEmailInput}
            onChange={handleCarrierEmailInputChange}
            onFocus={handleCarrierEmailInputFocus}
            onKeyPress={handleCarrierEmailKeyPress}
            placeholder="Add email manually or search by email..."
            className={`email-input ${carrierEmailError ? 'error' : ''}`}
          />
          
          {/* Autocomplete suggestions */}
          {showCarrierSuggestions && carrierSuggestions.length > 0 && (
            <div ref={carrierSuggestionsRef} className="email-suggestions">
              {carrierSuggestions.map((suggestion, index) => (
                <div
                  key={index}
                  className="suggestion-item"
                  onClick={() => handleCarrierSuggestionClick(suggestion)}
                >
                  <div className="suggestion-content">
                    <div className="suggestion-email">
                      <CustomIcon name="person" size={16} />
                      <span>{suggestion.email}</span>
                    </div>
                    {suggestion.carrier?.companyName && (
                      <div className="suggestion-company">
                        <CustomIcon name="building" size={14} />
                        <span>{suggestion.carrier.companyName}</span>
                        {suggestion.carrier.address?.city && (
                          <span className="suggestion-location">
                            • {suggestion.carrier.address.city}, {suggestion.carrier.address.state}
                          </span>
                        )}
                      </div>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
          
          {showCarrierSuggestions && carrierSuggestions.length === 0 && carrierEmailInput.length >= 2 && (
            <div ref={carrierSuggestionsRef} className="email-suggestions">
              <div className="no-results-message">
                No carriers found. You can still add this email manually.
              </div>
            </div>
          )}
        </div>
        
        {carrierEmailError && (
          <span className="email-error-message">{carrierEmailError}</span>
        )}
      </div>

      <div className="email-actions">
        {selectedCarrierEmails.length > 0 && (
          <button 
            type="button" 
            className="see-all-link"
            onClick={() => setShowAllCarrierEmails(!showAllCarrierEmails)}
          >
            {showAllCarrierEmails ? 'Hide added' : `Show added (${selectedCarrierEmails.length})`}
          </button>
        )}
        
        <button 
          type="button" 
          className="add-email-btn"
          onClick={() => addCarrierEmail(carrierEmailInput)}
          disabled={!carrierEmailInput.trim()}
        >
          <CustomIcon name="plus" size={16} />
          Add Email
        </button>
      </div>

      {showAllCarrierEmails && selectedCarrierEmails.length > 0 && (
        <div className="all-emails-list">
          <h4 className="all-emails-title">Added emails:</h4>
          <div className="all-emails-grid">
            {selectedCarrierEmails.map((email, index) => (
              <div
                key={index}
                className="available-email-item"
              >
                <CustomIcon name="person" size={16} />
                <span>{email}</span>
                <button 
                  type="button"
                  className="remove-email-small"
                  onClick={() => removeCarrierEmail(email)}
                  title="Remove email"
                >
                  <CustomIcon name="close" size={14} />
                </button>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default CarrierEmailsSection;
